// TODO
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { ApolloError, useApolloClient } from '@apollo/client';
import Bugsnag from '@bugsnag/js';
import { usePayments } from '@99designs/apps/spa/common/Payments/PaymentsContext';
import redirectTo from '@99designs/apps/spa/common/Ssr/redirectTo';
import {
    GetOrderPaymentRequestDocument,
    GetOrderPaymentRequestQuery,
    GetOrderPaymentRequestQueryVariables,
    ProductFixedPriceProjectFragment,
    useClaimBriefMutation,
    useLaunchBriefMutation,
    useUpdateBriefMutation,
} from '@99designs/apps/spa/pages/create/briefs/Brief.generated';
import BriefFooter, {
    Props as BriefFooterProps,
} from '@99designs/apps/spa/pages/create/components/BriefFooter';
import { LaunchState } from '@99designs/apps/spa/pages/create/components/LaunchModal';
import { Props as StepFooterProps } from '@99designs/apps/spa/pages/create/components/StepFooter';
import StepHeader from '@99designs/apps/spa/pages/create/components/StepHeader';
import { trackClickedNextButtonEvent } from '@99designs/apps/spa/pages/create/events';
import { redirectLaunchedBrief } from '@99designs/apps/spa/pages/create/util/routes';
import { BriefStep, StepState } from '@99designs/apps/spa/pages/create/util/step';
import { FormErrors, ObservableForm, PrintValidatorContext } from '@99designs/brief';
import { BriefContext, Order } from '@99designs/brief';
import { setCookieByName, stripParamFromUrl } from '@99designs/browser';
import { scrollToElement } from '@99designs/browser';
import { useFloatingAlert } from '@99designs/emu';
import { Source } from '@99designs/feature-auth';
import {
    BriefState,
    DesignerSource,
    PartnerCoupon,
    StructuredBriefValidationError,
} from '@99designs/graph-utils/types';
import { useAppConfig } from '@99designs/layout';
import { linkTo } from '@99designs/routing';
import { useUrlQuery } from '@99designs/spa-common';
import { useViewer } from '@99designs/viewer';
import { PARTNER_TRANSITION_PARAM } from '../common/components/ExternalBriefTransition';
import { getPaymentSource } from './PaymentSource';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FormData = Record<string, any>;

export interface Props {
    schema: string;
    steps: BriefStep[];
    currentStep: number;
    brief: BriefFooterProps['brief'] & {
        id: string;
        userId: number;
        designersWithSource: { source: DesignerSource }[];
        partnerCoupon?: PartnerCoupon | null;
        externalOrderItemId?: number | null;
        state: BriefState;
        errors?: StructuredBriefValidationError[];
    };
    formData: FormData;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    refetch: (options: { id: string }) => Promise<any>;
    disableFooterInteraction?: boolean;
    totalAllowableDesigners?: number;
    disableContacts?: boolean;
    briefProduct?: ProductFixedPriceProjectFragment | null;
    order?: Order | null;
    noPaymentRequiredHeading?: string;
    jobId?: string | null;
    postPaymentRedirect?: 'job';
}

export default function WrappedBriefWizard(props: Props) {
    const user = useViewer();

    const { steps, currentStep, brief } = props;

    const step = new StepState({ steps, currentStep, brief });

    // Prompt for login if authentication is required
    // Fires auth for Recommended flow
    if (!user && step.currentStep().isAuthRequired) {
        redirectTo('/signup', [
            { key: 'source', value: Source.PROJECTS_LAUNCH },
            { key: 'role', value: 'customer' },
            { key: 'skip-transition', value: 'true' },
            // post login we should not show the partner transition UI again, so the `fromPartner` param must be removed from the target
            {
                key: 'target',
                value: stripParamFromUrl(window.location.href, PARTNER_TRANSITION_PARAM),
            },
        ]);
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return <></>;
    }

    return <BriefWizard {...props} />;
}

function BriefWizard<T>(props: Props) {
    const [launchState, setLaunchState] = useState<LaunchState | undefined>();
    const [nextInProgress, setNextInProgress] = useState(false);
    const [saveInProgress, setSaveInProgress] = useState(false);
    const [error, setError] = useState<ApolloError | Error | undefined>();
    const [formData, setFormData] = useState(props.formData);
    const user = useViewer();
    const { displayErrors } = useAppConfig();
    const floatingAlert = useFloatingAlert();

    const {
        steps,
        currentStep,
        disableFooterInteraction,
        brief,
        totalAllowableDesigners = 5,
        disableContacts = false,
        briefProduct,
        order,
        noPaymentRequiredHeading,
        jobId,
        refetch,
        postPaymentRedirect,
    } = props;
    const client = useApolloClient();
    const [claimBrief] = useClaimBriefMutation();
    const [launchBrief] = useLaunchBriefMutation();
    const [updateBrief] = useUpdateBriefMutation();
    const payments = usePayments();
    const navigate = useNavigate();
    const [printValidator, setPrintValidator] = useState<() => string[]>();
    const step = new StepState({ steps, currentStep, brief });

    const { pathname: route } = useLocation();

    const form = useMemo(
        () =>
            new ObservableForm<FormData>(formData, {
                onSave: save,
                autosaveDelay: 1000,
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [route]
    );

    const { autolaunch, autocontinue } = useUrlQuery();

    useEffect(() => {
        if (autolaunch === '1') {
            launch();
        }

        if (autocontinue === '1') {
            handleNextClick();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (window.analytics) {
            window.analytics.page();
        }
    }, [route]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [currentStep]);

    useEffect(() => {
        if (brief.state === 'LAUNCHED') {
            redirectLaunchedBrief(brief);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // On load, if the current step requires authentication, try to claim the brief.
        // This is used in the 2FA flow which will redirect the user, authenticate them,
        // and then redirect back to the next step, before triggering this code.
        if (user && step.currentStep().isAuthRequired) {
            void claimIfUnclaimed();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (jobId) {
            navigate(linkTo.browseProjectsJobDesigners(jobId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId]);

    async function save(changes: Partial<T>): Promise<FormErrors> {
        try {
            setSaveInProgress(true);

            const res = await updateBrief({
                variables: {
                    id: brief.id,
                    // TODO: Remove this key now that we have a single dynamic brief input type.
                    fields: { dynamicBrief: changes },
                },
            });

            form.setData({
                ...form.data,
                ...changes,
            });

            setFormData(form.data);

            // Filter validation errors down to the fields on the current step
            const currentStepFields = step.currentStep().fields || [];
            if (res) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                return res
                    .data!.brief.errors.filter((e) => currentStepFields.includes(e.field))
                    .reduce<FormErrors>((errors, next) => {
                        errors[next.field] = next.reason;
                        return errors;
                    }, {});
            } else {
                return {};
            }
        } catch (error) {
            setError(error);
            floatingAlert({
                type: 'error',
                message: displayErrors
                    ? error
                    : __('Uh oh... Please try again or refresh the page.'),
            });
            return {};
        } finally {
            setSaveInProgress(false);
        }
    }

    async function claimIfUnclaimed() {
        if (!brief.userId) {
            await claimBrief({
                variables: { id: brief.id },
            });
        }
    }

    async function userInitiatedSave() {
        await claimIfUnclaimed();
        try {
            await form.save();
        } catch (error) {
            setError(error);
            floatingAlert({
                type: 'error',
                message: displayErrors
                    ? error
                    : __('Uh oh... Please try again or refresh the page.'),
            });
        }

        scrollToFirstError();
    }

    function scrollToFirstError() {
        const firstError = form.firstError();
        if (!firstError) {
            return;
        }
        form.scrollTo(firstError);
    }

    const handleSetPrintValidator = useCallback((validator: () => string[]) => {
        setPrintValidator(() => validator);
    }, []);

    async function handleNextClick() {
        setNextInProgress(true);
        let hasFormErrors = false;

        try {
            trackClickedNextButtonEvent({ id: brief.id });
            try {
                await form.save();
                await refetch({ id: brief.id });
            } catch (error) {
                setError(error);
                floatingAlert({
                    type: 'error',
                    message: displayErrors
                        ? error
                        : __('Uh oh... Please try again or refresh the page.'),
                });
            }

            if (form.hasErrors()) {
                hasFormErrors = true;
                scrollToFirstError();
            }

            if (printValidator !== undefined) {
                const errorFields = printValidator();

                if (!hasFormErrors && errorFields.length > 0) {
                    const element = document.getElementById(`print-option-${errorFields[0]}`);

                    return scrollToElement(element, 0);
                }
            }

            if (hasFormErrors) {
                return;
            }

            if (!user && step.nextRequiresAuth()) {
                // Fires auth for Brief flow
                redirectTo('/signup', [
                    { key: 'source', value: 'projects_launch' },
                    { key: 'role', value: 'customer' },
                    { key: 'skip-transition', value: 'true' },
                    { key: 'target', value: window.location.href + '?autocontinue=1' },
                ]);
                return;
            } else if (!error) {
                if (user) {
                    await claimIfUnclaimed();
                }

                navigate(step.nextUrl());
            }
        } finally {
            setNextInProgress(false);
        }
    }

    async function launch() {
        if (!isValid()) {
            handleErrors();
            return;
        }

        let launchTimeout;
        try {
            setNextInProgress(true);

            // Fires auth for Brief with Direct designer
            if (!user) {
                redirectTo('/signup', [
                    { key: 'source', value: 'projects_launch' },
                    { key: 'role', value: 'customer' },
                    { key: 'skip-transition', value: 'true' },
                    { key: 'target', value: window.location.href + '?autolaunch=1' },
                ]);
                return;
            }

            await userInitiatedSave();

            // We've had some reports of launches stalling without an error. This is currently
            // here to give us a picture of how many users are being affected by this.
            launchTimeout = setTimeout(
                () =>
                    Bugsnag.notify(new Error('Launch timeout'), (event) => {
                        event.addMetadata('brief', 'id', this.brief.id);
                    }),
                15000
            );

            const res = await launchBrief({
                variables: { id: brief.id },
            });

            if (res) {
                setLaunchState(LaunchState.Success);

                if (
                    res.data?.launchStructuredBrief.partnerCoupon &&
                    res.data.launchStructuredBrief.partnerCoupon.state === 'ALREADY_REDEEMED'
                ) {
                    setCookieByName({
                        cookieName: '_99pc',
                        expiryInMinutes: 0,
                    });
                }
            }
        } catch (error) {
            Bugsnag.notify(error, (event) => {
                event.context = 'Launch error';
                event.addMetadata('brief', 'id', brief.id);
            });

            setLaunchState(LaunchState.Error);
        } finally {
            if (launchTimeout) {
                clearTimeout(launchTimeout);
            }
            setNextInProgress(false);
        }
    }

    function getNextAction() {
        if (step.isInvoiceStep()) {
            return fetchOrderPaymentRequest;
        }

        if (step.hasStepAfter()) {
            return handleNextClick;
        }

        return launch;
    }

    function isValid() {
        return !brief.errors?.length;
    }

    function handleErrors() {
        if (!brief.errors?.length) {
            return;
        }
        const firstError = brief.errors[0];
        floatingAlert({
            type: 'error',
            message: `${firstError.field}: ${firstError.reason}`,
        });
        setNextInProgress(false);
    }

    async function fetchOrderPaymentRequest() {
        if (!isValid()) {
            handleErrors();
            return;
        }
        if (payments?.paymentModal) {
            payments.paymentModal.asyncShowPrepare();
        }

        setNextInProgress(true);

        // TODO we should get this from `create` - change required to support brief on vista
        const getRedirectPath = (): string => {
            // we need to pass through the current step as the step count is reduced
            // once payment is made
            if (step.hasStepAfter()) {
                return step.url();
            }

            // The BriefWizard component will do a redirect to the associated job
            if (postPaymentRedirect === 'job') {
                return step.url();
            }

            return linkTo.yourWork();
        };

        const { data, errors } = await client.query<
            GetOrderPaymentRequestQuery,
            GetOrderPaymentRequestQueryVariables
        >({
            query: GetOrderPaymentRequestDocument,
            variables: {
                id: brief.id,
                redirectPath: getRedirectPath(),
            },
            fetchPolicy: 'no-cache',
        });

        setNextInProgress(false);

        // TODO verify if launch permissions are applicable for this request
        if (errors) {
            Bugsnag.notify(new Error(errors[0].message));
            throw errors;
        }

        if (payments?.paymentModal && data && data.brief && data.brief.orderPaymentRequest) {
            payments.paymentModal.asyncShowFinish(data.brief.orderPaymentRequest);
        }
    }

    const stepFooterProps: StepFooterProps = {
        step,
        isAuthenticated: brief.userId > 0,
        nextInProgress,
        nextAction: getNextAction(),
        nextActionLabel: step.nextActionLabel(),
        disabled: nextInProgress,
        launchAction: launch,
    };

    const renderStep = step.currentStep().render;

    return (
        <BriefContext.Provider
            value={{
                brief: brief,
                product: briefProduct,
                order,
                paymentSource: getPaymentSource({
                    product: briefProduct,
                    partnerCoupon: brief.partnerCoupon,
                    order,
                }),
                schema: props.schema,
                totalAllowableDesigners: totalAllowableDesigners,
                disableContacts,
                noPaymentRequiredHeading,
            }}
        >
            <PrintValidatorContext.Provider value={{ setPrintValidator: handleSetPrintValidator }}>
                <StepHeader step={step} designers={brief.designers} briefProduct={briefProduct} />
                {renderStep && renderStep(form)}
                <BriefFooter
                    stepFooterProps={stepFooterProps}
                    launchState={launchState}
                    brief={brief}
                    saving={nextInProgress || saveInProgress}
                    disableFooterInteraction={disableFooterInteraction}
                />
            </PrintValidatorContext.Provider>
        </BriefContext.Provider>
    );
}
